// ════════════════════════════════════════════════════════════
// ProveedoresCompras — directorio de proveedores por categoría
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, TextField, Chip, Button, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Select,
  MenuItem, FormControl, InputLabel, FormControlLabel, Switch, CircularProgress, Typography,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  getCategoriasCompra, getProveedoresCompras, crearProveedorCompras, actualizarProveedor
} from '../api';

const REGIMENES = [
  { clave: 'nacional',         nombre: 'Nacional (España)' },
  { clave: 'intracomunitaria', nombre: 'Unión Europea' },
  { clave: 'importacion',      nombre: 'Importación (fuera de la UE)' },
];
const VACIO = {
  nombre: '', cif: '', categoria: 'recambios', contacto: '', email: '', telefono: '',
  direccion: '', pais: 'ES', regimen_compra: 'nacional', condiciones_pago: '',
  iban: '', notas: '', activo: 1,
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function ProveedoresCompras() {
  const [categorias, setCategorias] = useState<any[]>([]);
  const [lista, setLista] = useState<any[]>([]);
  const [categoria, setCategoria] = useState('');
  const [search, setSearch] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [edit, setEdit] = useState<any>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { getCategoriasCompra().then(setCategorias).catch(() => {}); }, []);

  const cargar = () => {
    setCargando(true);
    getProveedoresCompras({ categoria, search, incluir_inactivos: verInactivos ? 1 : 0 })
      .then(setLista).catch(() => setLista([]))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoria, search, verInactivos]);

  const nombreCat = (c: string) => categorias.find((x: any) => x.clave === c)?.nombre || c || '—';

  const guardar = async () => {
    if (!edit.nombre?.trim()) { alert('El proveedor necesita un nombre'); return; }
    setGuardando(true);
    try {
      const { id, n_pedidos, total_pedidos, ...datos } = edit;
      datos.activo = edit.activo ? 1 : 0;
      if (id) await actualizarProveedor(id, datos);
      else await crearProveedorCompras(datos);
      setEdit(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  const campo = (clave: string, etiqueta: string, ancho: number = 6, extra: any = {}) => (
    <Grid item xs={12} sm={ancho}>
      <TextField fullWidth size="small" label={etiqueta} value={edit?.[clave] ?? ''}
        onChange={(e) => setEdit({ ...edit, [clave]: e.target.value })} {...extra} />
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Buscar (nombre, CIF, contacto)" sx={{ flex: 1, minWidth: 220 }}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <FormControlLabel label="Ver inactivos"
          control={<Switch checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} />} />
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: '#4f46e5' }}
          onClick={() => setEdit({ ...VACIO })}>
          Nuevo proveedor
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Todas" onClick={() => setCategoria('')}
          color={categoria === '' ? 'primary' : 'default'} variant={categoria === '' ? 'filled' : 'outlined'} />
        {categorias.map((c: any) => (
          <Chip key={c.clave} label={c.nombre} onClick={() => setCategoria(c.clave)}
            color={categoria === c.clave ? 'primary' : 'default'}
            variant={categoria === c.clave ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {cargando ? <CircularProgress /> : lista.length === 0 ? (
        <Alert severity="info">No hay proveedores con esos criterios.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Proveedor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Categoría</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Contacto</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Régimen</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Pedidos</TableCell>
                <TableCell sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((p: any) => (
                <TableRow key={p.id} hover sx={{ opacity: p.activo === 0 ? 0.5 : 1 }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nombre}</Typography>
                    {p.cif && <Typography variant="caption" color="text.secondary">{p.cif}</Typography>}
                  </TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={nombreCat(p.categoria)} /></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="caption" display="block">{p.contacto || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[p.telefono, p.email].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="caption">
                      {REGIMENES.find(r => r.clave === (p.regimen_compra || 'nacional'))?.nombre}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{p.n_pedidos}</Typography>
                    <Typography variant="caption" color="text.secondary">{eur(p.total_pedidos)}</Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setEdit({ ...p })}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!edit} maxWidth="md" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setEdit(null); }}>
        <DialogTitle>{edit?.id ? `✏️ ${edit.nombre}` : '🏭 Nuevo proveedor'}</DialogTitle>
        <DialogContent>
          {edit && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {campo('nombre', 'Nombre / razón social *', 8)}
              {campo('cif', 'CIF / NIF / VAT', 4)}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Categoría</InputLabel>
                  <Select value={edit.categoria || 'recambios'} label="Categoría"
                    onChange={(e) => setEdit({ ...edit, categoria: e.target.value })}>
                    {categorias.map((c: any) => <MenuItem key={c.clave} value={c.clave}>{c.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Régimen fiscal</InputLabel>
                  <Select value={edit.regimen_compra || 'nacional'} label="Régimen fiscal"
                    onChange={(e) => setEdit({ ...edit, regimen_compra: e.target.value })}>
                    {REGIMENES.map(r => <MenuItem key={r.clave} value={r.clave}>{r.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {campo('contacto', 'Persona de contacto')}
              {campo('telefono', 'Teléfono', 3)}
              {campo('pais', 'País', 3)}
              {campo('email', 'Email')}
              {campo('condiciones_pago', 'Condiciones de pago')}
              {campo('direccion', 'Dirección', 12)}
              {campo('iban', 'IBAN (para los pagos)', 12)}
              {campo('notas', 'Notas', 12, { multiline: true, rows: 2 })}
              <Grid item xs={12}>
                <FormControlLabel label="Proveedor activo"
                  control={<Switch checked={edit.activo !== 0}
                    onChange={(e) => setEdit({ ...edit, activo: e.target.checked ? 1 : 0 })} />} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando} sx={{ bgcolor: '#4f46e5' }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}